import type { FleetCar } from '@ahd/shared/types/ahd-fleet-api/fleet-car/car';
import type { FleetConverterSettings } from '@ahd/shared/types/ahd-fleet-api/fleet-car/converter-settings';
import type { FleetCarKategorie, InfoGridFuelCategory } from '@ahd/shared/types/ahd-fleet-api/fleet-car/enums';
import type { Lang } from '@ahd/shared/types/ahd-fleet-api/lang';
import type { Filter } from '@ahd/shared/types/ahd-fleet/filter';
import type { IconifyIcon } from '@ahd/shared/types/misc/iconify-icon';

import { type Locale } from 'date-fns/locale';
import { type CSSProperties, type ReactNode } from 'react';

import { type Localization } from '@mui/material/locale';
import { type Components, type Theme, type TypographyVariantsOptions } from '@mui/material/styles';

import { type ConfigFilterPrefixes, type ConfigInfoGridPrefixes } from '../_tools/utils';

import type { ViewOptionsView } from '../hooks/use-car-search/types';
import type { Modules } from '../modules/_definition/types';

export enum ModuleViewType {
    ENDLESS = 'endless',
    NORMAL = 'normal',
    PAGING = 'paging',
}

export interface CatchCustomFields {
    /** Marketing campaign.
     * @default undefined
     */
    campaign_name?: string | undefined;
    /** CRM dealer code.
     * @default undefined
     */
    crm_dealercode?: string | undefined;
    /** CRM dealer id.
     * @default undefined
     */
    crm_dealerid?: string | undefined;
    /** DMS dealder code.
     * @default undefined
     */
    dms_dealercode?: string | undefined;
    /** Lead channel.
     * @default undefined
     */
    lead_channel?: string | undefined;
    /** Lead source.
     * @default undefined
     */
    lead_source?: string | undefined;
    /** Lead source 2.
     * @default undefined
     */
    lead_source2?: string | undefined;
    /** Lead type.
     * @default undefined
     */
    lead_type?: string | undefined;
    /** Location string.
     * @default undefined
     */
    locationString?: string | undefined;
    /** Marketing campaign.
     * @default undefined
     */
    marketing_campaign?: string | undefined;
    /** Marketing url.
     * @default undefined
     */
    marketing_url?: string | undefined;
    /** Url.
     * @default undefined
     */
    url?: string | undefined;
    /** Url without protocol.
     * @default false
     */
    urlWithoutProtocol?: boolean;
}

export type Language = {
    label: string;
    value: Lang;
    systemValue: Localization;
    icon: IconifyIcon;
    locale: Locale;
};

export enum TrackingProvider {
    ETracker = 'etracker',
    Octo = 'octo',
}

export type CarDataFields =
    | 'antrieb'
    | 'engine.hubraum'
    | 'engine.leistung'
    | 'engine.motor'
    | 'ez'
    | 'farbe'
    | 'garantie'
    | 'getriebe'
    | 'ident'
    | 'kategorie'
    | 'km'
    | 'leergewicht'
    | 'siegel'
    | 'siegel_garantie'
    | 'sitze'
    | 'spacer'
    | 'standort'
    | 'tueren'
    | 'typ'
    | 'verbrauch.text'
    | 'verbrauch.combined'
    | 'verbrauch.combinedDischarged'
    | 'verbrauch.combinedWeighted'
    | 'verbrauch.combinedWeightedShort'
    | 'verbrauch.primarySecondaryCombinedWeighted'
    | 'verbrauch.co2costsLow'
    | 'verbrauch.co2costsMedium'
    | 'verbrauch.co2costsHigh'
    | 'verbrauch.co2class'
    | 'verbrauch.effizienzBig'
    | 'verbrauch.effizienzDischarged'
    | 'verbrauch.effizienzWeighted'
    | 'verbrauch.emission'
    | 'verbrauch.emissionDischarged'
    | 'verbrauch.emissionWeighted'
    | 'verbrauch.emissionWeightedShort'
    | 'verbrauch.energycosts'
    | 'verbrauch.euronorm'
    | 'verbrauch.fuelCostsYear'
    | 'verbrauch.infoButton'
    | 'verbrauch.title'
    | 'verbrauch.wltp.low'
    | 'verbrauch.wltp.lowDischarged'
    | 'verbrauch.wltp.medium'
    | 'verbrauch.wltp.mediumDischarged'
    | 'verbrauch.wltp.high'
    | 'verbrauch.wltp.highDischarged'
    | 'verbrauch.wltp.extraHigh'
    | 'verbrauch.wltp.extraHighDischarged'
    | 'verbrauch.wltp.range';

export interface CarDataField {
    i: CarDataFields;
    fullWidth?: boolean;
    headingExtraLine?: boolean;
    icon: (car: FleetCar) => IconifyIcon;
    lineBreak: boolean;
    noTooltip?: boolean;
    noTypo?: boolean;
    renderTextValue?: (car: FleetCar) => string;
    renderValue?: (car: FleetCar) => ReactNode;
    t: string | ((car: FleetCar) => string);
    whiteSpace?: CSSProperties['whiteSpace'];
}

export type ConfigFilter =
    | 'hersteller'
    | 'modell'
    | 'typ'
    | 'kategorie'
    | 'aufbau'
    | 'siegel'
    | 'standort'
    | 'ort'
    | 'preis'
    | 'preisBis'
    | 'preisVon'
    | 'fd'
    | 'rate'
    | 'rateBis'
    | 'rateVon'
    | 'ez'
    | 'ezBis'
    | 'ezVon'
    | 'leistung'
    | 'leistungBis'
    | 'leistungVon'
    | 'km'
    | 'kmBis'
    | 'kmVon'
    | 'motor'
    | 'getriebe'
    | 'aus'
    | 'sofort'
    | 'aktion'
    | 'spacer'
    | 'tags'
    | 'farbe'
    | 'hasSeal'
    | 'tagsExclude'
    | 'seller';
export type ConfigFilterOther =
    | 'monatlicheRateButton'
    | 'sortButton'
    | 'shareButton'
    | 'advancedSearchButton'
    | 'resetSearchButton'
    | 'volltext'
    | 'gridviewButton'
    | 'listviewButton'
    | 'locationMapButton';

export type ShareButtons = 'telegram' | 'whatsapp';
export interface ConfigFilterObject {
    /** Order of shown filters in sidebar.
     * @default ['sofort', 'hersteller', 'modell', 'standort', 'preis', 'rate', 'ez', 'motor', 'getriebe', 'aus']
     */
    sidebar: ConfigFilter[];
    /** Order of shown filters on top.
     * @default ['[SM]hersteller', '[SM]modell', '[LG]sofort', '[LG]motor', 'getriebe', 'standort', 'preis', 'rate', 'ez', 'aus'],
     */
    top: (ConfigFilterPrefixes<ConfigFilter> | ConfigFilterOther)[];
}

export type ModuleCommonProps = {
    /** Default Filter.
     * @default undefined
     */
    defaultFilter: string | undefined;
    /** Default Filter ID.
     * @default undefined
     */
    defaultFilterId: string | undefined;
    /** Default Sort.
     * @default undefined
     */
    defaultSort: { order: Filter['order']; sort: Filter['sort'] } | undefined;
    /** Force filters to be set permanently.
     * @default { }
     */
    forcedFilter: Partial<Filter> & { onlyFacetts?: boolean };
    /** Force filters to be reset to a specific value, but not being forced to it.
     * @default { }
     */
    forcedDefaultFilter: Partial<Filter>;
    /** Limit the maximum column count.
     * @default undefined
     */
    limitCols: number | undefined;
    /** Turn off animations.
     * @default false
     */
    noAnimations: boolean;
    /** Select type of display
     * @default 'normal'
     */
    viewType: ModuleViewType;
};

/**
 * Configuration object.
 * @example
 * {
    general: {
        idGroup: 5,
        enable: true,
        metaTitleSuffix: 'Autohaus Liliensiek',
        searchPath: '/fahrzeugsuche',
    },
    contact: {
        isLeadSystem: true,
        linkDisclaimer: '/datenschutzerklaerung'
    },
    module: {
        aktionsslider: true,
        finanzierungsansicht: true,
        schnellsuche: true
    },
    fahrzeugsuche: {
        idExposee: 1572
    },
    style: {
        fontFamily: '"Muli", "Open Sans", "Arial", sans-serif',
        fontSize: 14,
        primaryColor: '#0099da',
    },
}
 */

/**
 * Debug-Level.
 * @example DebugLevel.NONE
 */
export enum DebugLevel {
    /** No debugging informations provided */
    NONE = 0,
    /** Enable debugging of cars */
    CARS = 1,
    /** Enable debugging of config */
    CONFIG = 2,
    /** Enable debugging of contact */
    CONTACT = 3,
    /** Enable debugging of externals (AutoUncle, WebCalc, …) */
    EXTERNALS = 4,
    /** Enable debugging of filters */
    FILTER = 5,
    /** Enable debugging of router, links, canonical and redirects */
    ROUTER = 6,
    /** Enable debugging of tracking */
    TRACKING = 7,
    /** Enable debugging of theme */
    THEME = 8,
    /** Enable info displaying and logging */
    INFO = 9,
    /** Enable warnings and info displaying and logging */
    WARNINGS = 10,
    /** Enable error, warnings and info displaying and logging */
    ERRORS = 11,
    /** Enable complete debugging mode completely */
    DEBUG = 12,
}

export interface ConfigObject {
    /** General configuration settings */
    general: {
        /**
         * External Cookies for Google Maps accepted on parent website.
         * @default false
         */
        acceptMapsExternal: boolean;
        /**
         * Enable additional settings and styles for AHD Web.
         * @default false
         */
        ahdWeb: boolean;
        /**
         * Enable Debug-Mode.
         * @default 'none'
         */
        debug: DebugLevel;
        /**
         * Disable the new SEO optimized routing system.
         * @default false
         */
        disableSeoRoutes: boolean;
        /**
         * Enable AHD Fleet.
         * @default false
         */
        enable: boolean;
        /**
         * Group ID.
         * @default undefined
         */
        idGroup: number | undefined;
        /**
         * Describes to use the landing page event_origin.
         * @default false
         */
        isLandingpage: boolean;
        /**
         * Used language for AHD Fleet.
         * @default 'de-DE'
         */
        language: Lang;
        /**
         * Prefix for Page-Title.
         * @default undefined
         */
        metaTitlePrefix: string | undefined;
        /**
         * Seperator for Page-Title.
         * @default ' | '
         */
        metaTitleSeperator: string;
        /**
         * Suffix for Page-Title.
         * @default undefined
         */
        metaTitleSuffix: string | undefined;
        /**
         * Use the mockup view (no live data will be used, just placeholder).
         * @default false
         */
        mockup: boolean;
        /**
         * Name of the project
         * @default undefined
         */
        projectName: string;
        /**
         * Path on the webpage, where the module "fahrzeugsuche".
         * @default '/fahrzeugsuche'
         */
        searchPath: string;
        /**
         * DEBUG ONLY - Path on the webpage, where the module "fahrzeugsuche" in dark mode.
         * @default '/fahrzeugsuche'
         */
        searchPathDark: string;
        /**
         * Additional needed search attributes for searchPath (e.x. for Joomla or Typo3 pages).
         * @default undefined
         */
        searchPathSearch: string | undefined;
        /**
         * Use takein sort instead of createdAt
         * @default undefined
         */
        useTakeinSort: boolean;
        /**
         * Display the location alias instead of the location name (car.loc).
         * @default false
         */
        useLocationAlias: boolean;
        /**
         * Enabled and defines the canonical root url followed by the seo optimized filter path.
         * @default undefined
         */
        useCanonicalLinks: boolean;
        // @ts-expect-error
    } & Pick<FleetConverterSettings, 'disableFinancing' | 'disableLeasing' | 'upeInsteadOfVk' | 'upeInsteadOfVkn' | 'useEyeCatcher'>;
    /** Contact Settings. */
    contact: {
        /** List of company IDs, that don't have lead system (only necessary for groups).
         * @default undefined
         */
        blacklistLeadsystem: number[] | undefined;
        /** Contact buttons settings.
         * @default { offsetX: 2, offsetY: 2, position: 'right' }
         */
        buttons: {
            invertColors: boolean;
            offsetX: number;
            offsetY: number;
            position: 'left' | 'right';
        };
        /** Order of shown filters.
         * @default ['hersteller', 'modell', 'ezVon', 'motor']
         */
        carSearchFilters: ConfigFilter[];
        /** Custom fiewlds for CATCH XML.
         * @default undefined
         */
        catchCustomFields: Omit<CatchCustomFields, 'lead_type'> | undefined;
        /** CTA button.
         * @default undefined
         */
        cta:
        | {
            /** Iconify Icon. **/
            icon: IconifyIcon;
            /** Target for link. **/
            target: HTMLAnchorElement['target'] | undefined;
            /** String for translate-function. **/
            title: string;
            /** URL. **/
            url: string;
        }
        | undefined;
        /** Is the mail receipient an automatic lead system?.
         * @default undefined
         */
        isLeadSystem: boolean | undefined;
        /** Is the mail receipient using a lead system from CATCH?.
         * @default undefined
         */
        isCatchLeadSystem: boolean | undefined;
        /** Link to the privacy statement.
         * @default undefined
         */
        linkDisclaimer: string | undefined;
        /** Requires phone number for contact form.
         * @default false
         */
        phoneRequired: boolean;
        /** When set, the "accept privacy" checkbox will be replaced with a text block, containing the specified text.
         * @default undefined
         */
        useTextInsteadOfPrivacyCheckBox: string | undefined;
    };
    /** Enable Modules. */
    module: Record<Exclude<Modules, Modules.ConfigListing | Modules.Install | Modules.Menu | Modules.Print>, boolean>;
    /** Settings for Aktionsslider. */
    [Modules.Aktionsslider]: ModuleCommonProps & {
        /** Adjust the slides per page, if there are less than expected.
         * @default false
         */
        adjustColNumberByListLength: boolean;
    };
    /** API Settings. */
    api: {
        /** AutoUncle Settings. */
        autoUncle: {
            /** Map for linking locations with the departmentIds.
             * @default undefined
             */
            departmentIds: Record<number, string>;
            /** Required for all Elements powered by AutoUncle.
             * @default undefined
             */
            id: string | undefined;
            /** Origin for Debugging.
             * @default undefined
             */
            origin: string | undefined;
            /** Enable Price Alert feature powered by AutoUncle.
             * @default false
             */
            priceAlert: boolean;
            /** Enable Price Rating Widget powered by AutoUncle.
             * @default false
             */
            priceRating: boolean;
            /** Required for TradeIn Widget/Button powered by AutoUncle.
             * @default undefined
             */
            sourceName: string | undefined;
            /** Enable TradeIn Widget/Button powered by AutoUncle.
             * @default false
             */
            tradeIn: boolean;
        };
        /** BDK Settings.*/
        bdk: {
            /** Enable BDK.
             * @default true
             */
            enabled: boolean;
            /** bdk-retailer-id must be included in the header for all BDK Calculator requests.
             * @default undefined
             */
            bdkRetailerId: string;
            /** BDK customer who does not use the order draft feature.
             * @default false
             */
            hasNoBdkOrderDraft: boolean;
        };
        /** It-Tecture UCL Zubehör Modul Settings */
        itTectureUcl: {
            /** Enable ITTecture API.
             * @default false
             */
            enabled: boolean;
            /** Location ID with an object of hersteller names and dealer ID assigned by It-Tecture.
             * @default undefined
             * @example { 12345: { 'audi': 'audi_79022', vw: 'vw_99901' } }
             */
            [locationId: number]: Record<string, string>;
        };
        /** Tracking Settings.*/
        tracking: {
            /** Api Key.
             * @default null
             */
            apiKey: string | null;
            /** Custom tracking event scripts as string.
             * @default undefined
             */
            customScripts: {
                /** Custom tracking event for clicking on a cta button.
                 * @default undefined
                 */
                ctaClick: string | undefined;
                /** Custom tracking event for clicking on a mail adress.
                 * @default undefined
                 */
                mailClick: string | undefined;
                /** Custom tracking event for clicking on the google maps link.
                 * @default undefined
                 */
                mapsClick: string | undefined;
                /** Custom tracking event for clicking on the price alert.
                 * @default undefined
                 */
                priceAlertClick: string | undefined;
                /** Custom tracking event for clicking on a phone number.
                 * @default undefined
                 */
                phoneClick: string | undefined;
                /** Custom tracking event for clicking on printing the exposee.
                 * @default undefined
                 */
                printClick: string | undefined;
                /** Custom tracking event for clicking on a share type.
                 * @default undefined
                 */
                shareClick: string | undefined;
                /** Custom tracking event for clicking on the trade in link.
                 * @default undefined
                 */
                tradeInClick: string | undefined;
                /** Custom tracking event for clicking on the website link.
                 * @default undefined
                 */
                websiteClick: string | undefined;
            } & (
                | {
                    /** Defines the handling (one function for all form types or splitted by form type).
                     * @default undefined
                     */
                    commonSuccess: true;
                    /** Custom tracking event for successfully sending a form (doesn't matter which type).
                     * @default undefined
                     */
                    formCommonSuccess: string | undefined;
                }
                | {
                    /** Defines the handling (one function for all form types or splitted by form type).
                     * @default undefined
                     */
                    commonSuccess: false;
                    /** Custom tracking event for successfully sending a form with a attached financial service.
                     * @default undefined
                     */
                    formFinancingSuccess: string | undefined;
                    /** Custom tracking event for successfully sending a form with type: general.
                     * @default undefined
                     */
                    formGeneralSuccess: string | undefined;
                    /** Custom tracking event for successfully sending a form with type: test drive.
                     * @default undefined
                     */
                    formTestDriveSuccess: string | undefined;
                }
            );
            /** Enable Tracking.
             * @default true
             */
            enabled: boolean;
            /** Tracking provider.
             * @default 'ga'
             */
            provider: TrackingProvider;
        };
        /** WebCALC Settings.*/
        webcalc: {
            /** Enable WebCALC.
             * @default true
             */
            enabled: boolean;
            /** Domain WebCALC.
             * @default 'VW.NEW'
             */
            domain: string;
        };
    };
    /** Settings for Aktionsslider. */
    cartile: {
        /** Choose visible data fields in grid for each field type
         * @default { big: { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }, small: { default: ['verbrauch.combined'] } }
         */
        infoGrid: {
            /** Choose visible data fields in grid
             * [G] at beginning for gridOnly
             * [L] at beginning for listOnly
             * null means, that the field type shpuld not be visible
             * undefined means, that there is no specific config for this category and the default should be used
             * @default { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }
             */
            big: Record<Exclude<FleetCarKategorie, FleetCarKategorie.PKW> | 'default', Record<InfoGridFuelCategory, ConfigInfoGridPrefixes<CarDataFields>[] | null | undefined>>;
            /** Choose visible data fields in grid
             * [G] at beginning for gridOnly
             * [L] at beginning for listOnly
             * null means, that the field type shpuld not be visible
             * undefined means, that there is no specific config for this category and the default should be used
             * @default { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }
             */
            small: Record<Exclude<FleetCarKategorie, FleetCarKategorie.PKW> | 'default', Record<InfoGridFuelCategory, ConfigInfoGridPrefixes<CarDataFields>[] | null | undefined>>;
            /** Choose visible data fields in grid
             * [G] at beginning for gridOnly
             * [L] at beginning for listOnly
             * null means, that the field type shpuld not be visible
             * undefined means, that there is no specific config for this category and the default should be used
             * @default { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }
             */
            list: Record<Exclude<FleetCarKategorie, FleetCarKategorie.PKW> | 'default', Record<InfoGridFuelCategory, ConfigInfoGridPrefixes<CarDataFields>[] | null | undefined>>;
        };
        /** Show labels and order them
         * @default []
         */
        labels: ('availableAt' | 'aktion' | 'autoaboAvailableAt' | 'commercial' | 'leasing')[];
    };
    /** Settings for ErweiterteSchnellsuche. */
    [Modules.ErweiterteSchnellsuche]: ModuleCommonProps & {
        /** Adjust the slides per page, if there are less than expected.
         * @default false
         */
        adjustColNumberByListLength: boolean;
        /** Display the filters full-width with the search button below in a new row
         * @default false
         */
        buttonBelowFilters: boolean;
        /** Order of shown filters in Schnellsuche.
         * @default ['hersteller', 'modell']
         */
        filter: (ConfigFilterPrefixes<ConfigFilter> | ConfigFilterOther)[];
        /** Show one filter per row.
         * @default false
         */
        oneFilterPerRow: boolean;
    };
    /** Settings for Fahrzeugslider. */
    [Modules.Fahrzeugslider]: Omit<ModuleCommonProps, 'viewType'> & {
        /** Adjust the slides per page, if there are less than expected.
         * @default false
         */
        adjustColNumberByListLength: boolean;
        /** Order of shown filters in Schnellsuche.
         * @default ['hersteller', 'modell']
         */
        filter: (ConfigFilterPrefixes<ConfigFilter> | ConfigFilterOther)[];
    };
    /** Settings for Fahrzeugliste. */
    [Modules.Fahrzeugliste]: ModuleCommonProps;
    /** Settings for Fahrzeugsuche. */
    [Modules.Fahrzeugsuche]: Omit<ModuleCommonProps, 'viewType'> & {
        /** Replace filter.sofort label with a custom string.
         * @default undefined
         */
        customNameSofort: string | undefined;
        /** Show vehicle details only as single row.
         * @default true
         */
        detailsSingleRow: boolean;
        /** Order of shown filters.
         * @default {
         *  sidebar: ['hersteller', 'modell', 'standort', 'preis', 'rate', 'ez', 'motor', 'getriebe', 'aus'],
         *  top: ['hersteller', 'modell', 'motor', 'getriebe', 'standort', 'preis', 'rate', 'ez', 'aus']
         * }
         */
        filter: ConfigFilterObject;
        /** Start displaying car pictures from the specified index.
         * @default undefined
         */
        firstImageIndex: number | undefined;
        /** Hide contact partners in location tab at vehicle detail view.
         * @default false
         */
        hideContactPersons: boolean;
        /** Hide autohaus-digital link in footer.
         * @default false
         */
        hideFooterAutohausDigitalLink: boolean;
        /** Hide hint about fuel usage data in footer.
         * @default false
         */
        hideFooterVerbrauch: boolean;
        /** Hide heading if page already has customer defined h1.
         * @default true
         */
        hideHeading: boolean;
        /** Hide Probefahrt FAB & textlink.
         * @default false
         */
        hideProbefahrt: boolean;
        /** Print ID for Exposee.
         * @default 1521
         */
        idExposee: number;
        /** Show List view by default.
         * @default false
         */
        listView: ViewOptionsView;
        /** Show the defined contact links in LocationInfo.
         * @default ['fax', 'googleMaps', 'mail', 'opening', 'phone', 'website']
         */
        locationInfo: ('phone' | 'mail' | 'fax' | 'googleMaps' | 'opening' | 'website')[];
        /** Original h1 from page.
         * @default ''
         */
        originalH1: string;
        /** Order of shown filters.
         * @default undefined
         */
        shareButtons: ShareButtons[] | undefined;
        /** Add Design to Title in List.
         * @default false
         */
        titleWithDesign: boolean;
        /** Puts the Carlist-Filter on the top.
         * @default false
         */
        topFilter: boolean;
        /** Trade-In-Link.
         * @default undefined
         */
        tradeInLink: string | undefined;
        /** Use location search map
         * @default false
         */
        useLocationMap: boolean;
        /** Use raw google maps colors.
         * @default false
         */
        useRawGoogleMapsColors: boolean;
        /** Select type of display
         * @default 'paging'
         */
        viewType: Exclude<ModuleViewType, ModuleViewType.NORMAL>;
    };
    /** Settings for detail view of car?. */
    fahrzeugdetail: {
        /** Additional infos under infogrid.
         * @default []
         */
        additionalInfo: {
            /** Icon name.
             * @default ''
             */
            icon?: string;
            /** String for translate-function.
             * @default ''
             */
            content: string;
        }[];
        /** Disable showing fuel usage data.
         * @default false
         */
        hideVerbrauch: boolean;
        /** Choose visible data fields in grid for each field type
         * @default { big: { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }, small: { default: ['verbrauch.combined'] } }
         */
        infoGrid: {
            /** Choose visible data fields in grid
             * [G] at beginning for gridOnly
             * [L] at beginning for listOnly
             * null means, that the field type shpuld not be visible
             * undefined means, that there is no specific config for this category and the default should be used
             * @default { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }
             */
            big: Record<Exclude<FleetCarKategorie, FleetCarKategorie.PKW> | 'default', Record<InfoGridFuelCategory, CarDataFields[] | null | undefined>>;
            /** Choose visible data fields in grid
             * [G] at beginning for gridOnly
             * [L] at beginning for listOnly
             * null means, that the field type shpuld not be visible
             * undefined means, that there is no specific config for this category and the default should be used
             * @default { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }
             */
            small: Record<Exclude<FleetCarKategorie, FleetCarKategorie.PKW> | 'default', Record<InfoGridFuelCategory, CarDataFields[] | null | undefined>>;
            /** Choose visible data fields in grid
             * [G] at beginning for gridOnly
             * [L] at beginning for listOnly
             * null means, that the field type shpuld not be visible
             * undefined means, that there is no specific config for this category and the default should be used
             * @default { default: ['ez', 'km', 'ident', 'engine.leistung', 'engine.motor', 'getriebe', 'siegel', 'farbe', 'antrieb'] }
             */
            list: Record<Exclude<FleetCarKategorie, FleetCarKategorie.PKW> | 'default', Record<InfoGridFuelCategory, CarDataFields[] | null | undefined>>;
        };
        /** Order of Tabs to change default of ausstattung, beschreibung, verbrauchsangaben, standort
         * @default []
         */
        orderTabs: string[];
        /** Enable showing the location image in the location details.
         * @default true
         */
        showLocationImage: boolean;
        /** Force permanent filters for "Ähnliche Fahrzeuge"
         * @default undefined
         */
        similarCarsFilter: ModuleCommonProps['forcedFilter'];
        /** Choose variant of detail page.
         * @default 1
         */
        variant: 1;
    };
    /** Settings for Finanzierungsansicht. */
    [Modules.Finanzierungsansicht]: ModuleCommonProps & {
        /** Order of shown filters in Finanzierungsansicht.
         * @default ['monatlicheRateButton', 'sortButton']
         */
        filter: (ConfigFilterPrefixes<ConfigFilter> | ConfigFilterOther)[];
    };
    /** Settings for Schnellsuche. */
    [Modules.Schnellsuche]: Omit<ModuleCommonProps, 'viewType'> & {
        /** Display the filters full-width with the search button below in a new row
         * @default false
         */
        buttonBelowFilters: boolean;
        /** Order of shown filters in Schnellsuche.
         * @default ['hersteller', 'modell']
         */
        filter: (ConfigFilterPrefixes<ConfigFilter> | ConfigFilterOther)[];
        /** Turn off border and padding.
         * @default false
         */
        noBorderPadding: boolean;
        /** Show one filter per row.
         * @default false
         */
        oneFilterPerRow: boolean;
    };
    /** Settings for Standorte. */
    [Modules.Standorte]: Omit<ModuleCommonProps, 'viewType'> & {
        /** Use raw google maps colors.
         * @default false
         */
        useRawGoogleMapsColors: boolean;
    };
    /** Style Settings. */
    style: {
        /** Select different border radius for all elements.
         * @default 2
         */
        borderRadius: number;
        /** Enable dark mode.
         * @default false
         */
        darkMode: boolean;
        /** Disable Slider border shadow.
         * @default false
         */
        disableSliderShadow: boolean;
        /** Default Font Family.
         * @default null
         */
        fontFamily: string | null;
        /** Default Font Size for small and large displays.
         * @default 16
         */
        fontSize: number;
        /** Override the global Material-UI components settings for customization. */
        muiComponents: Components<Theme>;
        /** Override the global Material-UI typography settings for customization. */
        muiTypography: TypographyVariantsOptions;
        /** Turn off animations.
         * @default false
         */
        noAnimations: boolean;
        /** Primary Color.
         * @default #0d1b48
         */
        primaryColor: `#${string}`;
        /** Set offset for auto scroll to top.
         * @default undefined
         */
        scrollOffset: number | undefined;
        /** Secondary Color (currently not in use).
         * @default #c33c54
         */
        secondaryColor: `#${string}`;
    };
    /** Cookie Settings. */
    cookies: {
        /** Enable analysis cookies.
         * @default true
         */
        analysis: boolean;
        /** Enable functional cookies.
         * @default false
         */
        functional: boolean;
        /** Enable marketing cookies.
         * @default false
         */
        marketing: boolean;
        /** Enable necessary cookies.
         * @default false
         */
        necessary: boolean;
    };
}
